import type { SQLiteDatabase } from 'expo-sqlite';

import type { ItemCondition } from '@/db/types';
import { parseLocalDate } from '@/lib/dates';
import { addInventoryItem, type NewInventoryInput } from '@/lib/inventory';

/**
 * Bulk import for inventory. Parses a CSV string with a tolerant column
 * mapper, validates rows, and inserts the valid ones. Designed for
 * spreadsheets people already keep — common column names from Excel,
 * Numbers, and the standard "Home Inventory" templates all resolve.
 *
 * Required: name. Everything else is optional.
 *
 * Accepted columns (case-insensitive, spaces / underscores / hyphens
 * interchangeable):
 *   name | item | description
 *   room | location
 *   category | type
 *   brand | make | manufacturer
 *   model | model number | model#
 *   serial | serial number | s/n
 *   vendor | store | retailer | purchased from
 *   purchase date | bought | date | purchased
 *   purchase price | price | cost | paid
 *   current value | value | replacement
 *   condition (excellent / good / fair / poor)
 *   insured (yes/no/true/false/1/0)
 *   notes | description | comments
 */

export interface CsvImportResult {
  inserted: number;
  skipped: number;
  errors: { row: number; message: string }[];
}

const COLUMN_ALIASES: Record<keyof RawRow, string[]> = {
  name: ['name', 'item', 'item name'],
  room: ['room', 'location', 'where'],
  category: ['category', 'type', 'kind'],
  brand: ['brand', 'make', 'manufacturer'],
  model: ['model', 'model number', 'model#', 'model no'],
  serial: ['serial', 'serial number', 's/n', 'sn'],
  vendor: ['vendor', 'store', 'retailer', 'purchased from', 'bought from'],
  purchase_date: ['purchase date', 'bought', 'date', 'purchased', 'date purchased'],
  purchase_price: ['purchase price', 'price', 'cost', 'paid', 'amount'],
  current_value: ['current value', 'value', 'replacement value', 'worth'],
  condition: ['condition', 'state'],
  insured: ['insured', 'is insured', 'covered'],
  notes: ['notes', 'description', 'comments', 'memo'],
};

interface RawRow {
  name?: string;
  room?: string;
  category?: string;
  brand?: string;
  model?: string;
  serial?: string;
  vendor?: string;
  purchase_date?: string;
  purchase_price?: string;
  current_value?: string;
  condition?: string;
  insured?: string;
  notes?: string;
}

export interface CsvPreviewRow {
  index: number;
  ok: boolean;
  reason?: string;
  parsed?: Omit<NewInventoryInput, 'propertyId'>;
}

export function parseInventoryCsv(text: string): CsvPreviewRow[] {
  const rows = parseCsv(text);
  if (rows.length === 0) return [];
  const header = rows[0].map((c) => normalizeKey(c));
  const colMap = mapColumns(header);

  const out: CsvPreviewRow[] = [];
  for (let i = 1; i < rows.length; i++) {
    const cells = rows[i];
    if (cells.length === 0 || cells.every((c) => c.trim() === '')) continue;
    const raw: RawRow = {};
    for (const [key, idx] of Object.entries(colMap)) {
      if (idx == null) continue;
      raw[key as keyof RawRow] = cells[idx]?.trim();
    }
    const name = raw.name?.trim();
    if (!name) {
      out.push({ index: i, ok: false, reason: 'Missing name' });
      continue;
    }
    const parsed: Omit<NewInventoryInput, 'propertyId'> = {
      name,
      room: raw.room?.trim() || null,
      category: raw.category?.trim() || null,
      brand: raw.brand?.trim() || null,
      model: raw.model?.trim() || null,
      serial_number: raw.serial?.trim() || null,
      vendor: raw.vendor?.trim() || null,
      purchase_date: parseDate(raw.purchase_date),
      purchase_cents: parseCents(raw.purchase_price),
      value_cents: parseCents(raw.current_value),
      condition: parseCondition(raw.condition),
      is_insured: parseBool(raw.insured) ? 1 : 0,
      photo_uri: null,
      notes: raw.notes?.trim() || null,
    };
    out.push({ index: i, ok: true, parsed });
  }
  return out;
}

export async function importInventoryCsv(
  db: SQLiteDatabase,
  propertyId: string,
  rows: CsvPreviewRow[]
): Promise<CsvImportResult> {
  const result: CsvImportResult = { inserted: 0, skipped: 0, errors: [] };
  await db.withTransactionAsync(async () => {
    for (const r of rows) {
      if (!r.ok || !r.parsed) {
        result.skipped += 1;
        if (r.reason) result.errors.push({ row: r.index, message: r.reason });
        continue;
      }
      try {
        await addInventoryItem(db, { ...r.parsed, propertyId });
        result.inserted += 1;
      } catch (err) {
        result.skipped += 1;
        result.errors.push({
          row: r.index,
          message: err instanceof Error ? err.message : String(err),
        });
      }
    }
  });
  return result;
}

// --- parsing helpers -------------------------------------------------------

function normalizeKey(s: string): string {
  return s.toLowerCase().replace(/[\s_\-]+/g, ' ').trim();
}

function mapColumns(header: string[]): Partial<Record<keyof RawRow, number>> {
  const out: Partial<Record<keyof RawRow, number>> = {};
  for (const [key, aliases] of Object.entries(COLUMN_ALIASES) as [keyof RawRow, string[]][]) {
    for (const alias of aliases) {
      const idx = header.indexOf(alias);
      if (idx >= 0) {
        out[key] = idx;
        break;
      }
    }
  }
  return out;
}

function parseDate(raw: string | undefined): number | null {
  if (!raw) return null;
  const t = parseLocalDate(raw);
  return isNaN(t) ? null : t;
}

function parseCents(raw: string | undefined): number | null {
  if (!raw) return null;
  const cleaned = raw.replace(/[$,\s]/g, '');
  const n = parseFloat(cleaned);
  return isNaN(n) ? null : Math.round(n * 100);
}

function parseCondition(raw: string | undefined): ItemCondition | null {
  if (!raw) return null;
  const v = raw.trim().toLowerCase();
  if (v.startsWith('exc')) return 'excellent';
  if (v.startsWith('good')) return 'good';
  if (v.startsWith('fair')) return 'fair';
  if (v.startsWith('poor')) return 'poor';
  return null;
}

function parseBool(raw: string | undefined): boolean {
  if (!raw) return false;
  const v = raw.trim().toLowerCase();
  return v === 'yes' || v === 'true' || v === '1' || v === 'y';
}

/**
 * Tiny RFC 4180-ish CSV parser. Handles quoted cells with embedded commas
 * and double-quote escapes. Doesn't try to support all CSV dialects —
 * good enough for spreadsheets people export and pasteable inventory
 * lists.
 */
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  let i = 0;
  while (i < text.length) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      cell += c;
      i += 1;
      continue;
    }
    if (c === '"') { inQuotes = true; i += 1; continue; }
    if (c === ',') { row.push(cell); cell = ''; i += 1; continue; }
    if (c === '\r') { i += 1; continue; }
    if (c === '\n') {
      row.push(cell); cell = '';
      rows.push(row);
      row = [];
      i += 1;
      continue;
    }
    cell += c;
    i += 1;
  }
  if (cell.length > 0 || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  return rows;
}
